import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import routes from 'routes';
import { getMeetSummary } from 'api/clubApi';
import { columns } from 'config/admin/event';
import { eventNamesInOrder } from 'utils/eventListOrder';
import useSessionFilters from 'hooks/useSessionFilters';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import {
  fetchAllAgeView,
  fetchAllEventView,
} from 'services/admin/EventService';
import { convertDateTime } from 'utils/time';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import EventDataTable from 'components/admin/event/EventDataTable';
import ResultsViewSwitch from 'components/admin/event/ResultsViewSwitch';
import EventPopup from 'components/admin/EventPopup';
import ExportFormatDialog from 'components/admin/ExportFormatDialog';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateSelectedAgeResult,
  updateSelectedEvent,
} from 'services/admin/Event/eventSlice';
import { exportResultsToFile, updateEventPublishingStatusWithMeetID } from 'api/resultApi';
import { UserPermissionLevel } from 'constants/auth';
import { exportAndDownloadFile } from 'utils/fileDownload';
import filterStyles from 'components/admin/bulkAdminTable.module.css';
import styles from './Events.module.css';
import { clubForId } from 'utils/clubFeatures';
import {
  GROUPING_BASIS,
  groupingBasisOf,
  groupWords,
  groupKeyOfName,
  formatCombinedGroup,
  formatGroupName,
} from 'utils/groupLabel';

// Both list endpoints return gender as the stored code, so the dropdown maps
// codes to the words used elsewhere in the UI. The order is the display order.
const GENDER_LABELS = { F: 'Female', M: 'Male', Mixed: 'Mixed' };
const GENDER_ORDER = ['F', 'M', 'Mixed'];

// Age view reports ages as bare numbers ("10") while event view carries the
// label the results were uploaded with ("U10" or "10", or "Y5" for a club that
// groups by year level). groupKeyOfName (utils/groupLabel.js) gives both the
// same key, so one stored selection matches in both views.

const FILTER_DEFAULTS = { eventType: 'all', ageGroup: 'all', gender: 'all' };

const Events = ({ isOpenEditPopup = false }) => {
  const userPermissionLevel = getUserPermissionLevel();
  const { clubId, meetId, viewType } = useParams();

  // How this meet's club groups its athletes, from the club list already in
  // memory: it decides the words (Age or Year) and how a group is shown.
  const groupingBasis = useSelector((state) =>
    groupingBasisOf(clubForId(state.user, clubId))
  );
  const words = groupWords(groupingBasis);
  const navigate = useNavigate();

  // Toolbar filters — persisted in sessionStorage per meet so they survive
  // the round trip into a result page and back, the Age/Event view toggle,
  // and the refetch after Mark as Preliminary/Final.
  const [filters, setFilter] = useSessionFilters(
    `meetResultsFilters_${meetId}`,
    FILTER_DEFAULTS
  );

  const [fetchingEvents, setFetchingEvents] = useState(true);
  const [fetchingMeet, setFetchingMeet] = useState(true);
  // True while the current view's list is being fetched, so an empty list is
  // not reported before the answer is in.
  const [listLoading, setListLoading] = useState(false);

  const [viewOption, setViewOption] = useState(null);
  const [hasAgeEvents, setHasAgeEvents] = useState(false);
  const [meet, setMeet] = useState(null);
  const [events, setEvents] = useState([]);
  const [preliminaryTag, setPreliminaryTag] = useState(false);
  const [showPopup, setShowPopup] = React.useState(isOpenEditPopup);
  // Export asks only for the file's format; it exports what the filters show
  // (Doug, 2026-10-25: the old export dialog with its own event list is gone).
  const [showExportDialog, setShowExportDialog] = useState(false);

  const [csvFormat, setCsvFormat] = useState('rhq_excel');
  const [editMode, setEditMode] = useState('add');
  const dispatch = useDispatch();

  // Whether the address names a view. Read through a ref inside fetchAllEvents
  // so that callback does not re-fire when the address changes ahead of
  // viewOption (see the note on its dependency list).
  const viewTypeRef = useRef(viewType);
  viewTypeRef.current = viewType;

  // Resolve viewType: explicit URL value wins; otherwise fall back to
  // meet.hasAgeEvents once the meet metadata is available.
  useEffect(() => {
    if (viewType) {
      setViewOption(viewType);
    } else if (meet) {
      setViewOption(meet.hasAgeEvents ? 'age-view' : 'event-view');
    }
  }, [viewType, meet]);

  const fetchAllEvents = useCallback(async () => {
    if (!viewOption) return;
    const handleEventView = (event, publishingStatus) => {
      dispatch(updateSelectedAgeResult(null));
      dispatch(
        updateSelectedEvent({
          ...event,
          publishingStatus,
        })
      );
      if (viewOption === 'age-view') {
        const firstRoundType = event.rounds?.[0]?.roundType;
        navigate(
          routes.admin.ageView.url(
            clubId,
            meetId,
            event.eventType,
            event.ageGroup,
            event.gender,
            firstRoundType
          )
        );
      } else {
        const firstRoundEventID = event.rounds?.[0]?.eventID;
        navigate(
          routes.admin.eventRound.url(
            clubId,
            meetId,
            event.eventType,
            firstRoundEventID
          )
        );
      }
    };
    // True while this view's list is being fetched. It stays true when the
    // fetch hands over to the other view (below), until that view has loaded.
    let handedOver = false;
    setListLoading(true);
    try {
      if (viewOption === 'age-view') {
        setHasAgeEvents(true);
        const ageRowCount = await fetchAllAgeView(
          clubId,
          meetId,
          setEvents,
          editEventType,
          handleEventView,
          navigate
        );
        // meet.hasAgeEvents is true as soon as the meet's program has youth
        // groups, but the age view is built from results. So a meet with no
        // results yet lands on an empty age view, and an event added by hand
        // can never show there. When the page chose the age view itself (the
        // address did not ask for it), show the event view instead: it lists
        // every event, with or without results.
        if (ageRowCount === 0 && !viewTypeRef.current) {
          handedOver = true;
          setViewOption('event-view');
        }
      } else {
        await fetchAllEventView(
          clubId,
          meetId,
          setEvents,
          editEventType,
          handleEventView,
          navigate,
          setHasAgeEvents
        );
      }
    } catch (err) {
      console.error('[Events] Fetch published events error: ', err);
    } finally {
      if (!handedOver) setListLoading(false);
    }
    // navigate and dispatch are excluded — they can be unstable across URL
    // updates and would cause this callback to re-fire with the old viewOption
    // before the URL→viewOption sync completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, meetId, viewOption]);

  const fetchMeet = useCallback(async () => {
    try {
      const response = await getMeetSummary(clubId, meetId);
      setMeet(response.data);
    } catch (err) {
      console.log('[Events] Fetch Meets error: ', err);
    }
  }, [clubId, meetId]);

  const handleBackToMeet = () => {
    navigate(routes.admin.showMeet.url(clubId, meetId));
  };

  const handleAddEvent = () => {
    setEditMode('add');
    setShowPopup(true);
    // navigate(routes.admin.addEvent.url(clubId, meetId));
  };

  const changeViewOption = (viewOption) => {
    // setViewOption(viewOption);
    navigate(routes.admin.events.url(clubId, meet.meetID, viewOption));
  };

  useEffect(() => {
    fetchAllEvents().then(() => {
      setFetchingEvents(false);
    });
  }, [fetchAllEvents]);

  useEffect(() => {
    fetchMeet().then(() => {
      setFetchingMeet(false);
    });
  }, [fetchMeet]);

  useEffect(() => {
    if (events.length > 0) {
      setPreliminaryTag(
        events.some((e) => e.publishingStatus === 'Preliminary')
      );
    }
  }, [events]);

  // Dropdown options come from the loaded rows, so nobody can pick a value
  // with nothing behind it. Events are in the rows' order, which is the
  // club's event type order (utils/eventListOrder).
  const eventOptions = useMemo(() => eventNamesInOrder(events), [events]);

  // Keyed by the normalized age so "U10" and "10" are one option; the label
  // is whatever the current view shows in its Age Group column. A combined
  // group (step 8) is keyed by its numbers ("14-17") and reads its name and range.
  const ageOptions = useMemo(() => {
    const byKey = new Map();
    events.forEach((row) => {
      const key = groupKeyOfName(groupingBasis, row.ageGroup);
      if (key && !byKey.has(key)) {
        byKey.set(
          key,
          row.groupName
            ? formatCombinedGroup(groupingBasis, row.groupName, row.ageGroup)
            : formatGroupName(groupingBasis, row.ageGroup)
        );
      }
    });
    return [...byKey.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => {
        const aNum = parseInt(a.value);
        const bNum = parseInt(b.value);
        if (isNaN(aNum) && isNaN(bNum)) return a.value.localeCompare(b.value);
        if (isNaN(aNum)) return 1;
        if (isNaN(bNum)) return -1;
        return aNum - bNum;
      });
  }, [events, groupingBasis]);

  const genderOptions = useMemo(() => {
    const present = new Set(events.map((row) => row.gender).filter(Boolean));
    const known = GENDER_ORDER.filter((code) => present.has(code));
    const extras = [...present]
      .filter((code) => !GENDER_ORDER.includes(code))
      .sort();
    return [...known, ...extras].map((code) => ({
      value: code,
      label: GENDER_LABELS[code] || code,
    }));
  }, [events]);

  // A stored value the current rows don't contain (the other view, or data
  // that changed since) acts as All rather than leaving the select dangling.
  // The stored value itself is kept so it reapplies when its rows come back.
  const eventTypeFilter = eventOptions.includes(filters.eventType)
    ? filters.eventType
    : 'all';
  const ageGroupFilter = ageOptions.some((o) => o.value === filters.ageGroup)
    ? filters.ageGroup
    : 'all';
  const genderFilter = genderOptions.some((o) => o.value === filters.gender)
    ? filters.gender
    : 'all';

  const filteredEvents = useMemo(
    () =>
      events.filter(
        (row) =>
          (eventTypeFilter === 'all' || row.eventTypeValue === eventTypeFilter) &&
          (ageGroupFilter === 'all' ||
            groupKeyOfName(groupingBasis, row.ageGroup) === ageGroupFilter) &&
          (genderFilter === 'all' || row.gender === genderFilter)
      ),
    [events, eventTypeFilter, ageGroupFilter, genderFilter, groupingBasis]
  );

  // The group column is headed in the club's words. For a club that groups by
  // year level the Year View's bare group number is shown as the year (10 as
  // Y5); the column still sorts on the stored value. An age club's column
  // shows its value as it is. A combined group's row (step 8), in any club,
  // reads its name and range: "Junior (8-10)", "U14-U17", "Year 11/12 (Y11-Y12)".
  const tableColumns = useMemo(
    () =>
      columns.map((column) => {
        if (column.accessor !== 'ageGroup') return column;
        const yearLevel = groupingBasis === GROUPING_BASIS.YEAR_LEVEL;
        return {
          ...column,
          label: words.group,
          Cell: (row) => {
            if (row.groupName) {
              return formatCombinedGroup(groupingBasis, row.groupName, row.ageGroup);
            }
            return yearLevel
              ? formatGroupName(groupingBasis, row.ageGroup)
              : row.ageGroup;
          },
        };
      }),
    [groupingBasis, words]
  );

  const clearFilters = () => {
    setFilter('eventType', 'all');
    setFilter('ageGroup', 'all');
    setFilter('gender', 'all');
  };

  const closeEditPopup = () => {
    setShowPopup(false);
    navigate(routes.admin.events.url(clubId, meetId, viewOption));
  };

  // The rows the filters show, and every one of their rounds. With no filter
  // the file holds every event of the meet, as it did when all were ticked.
  // The server reads an empty list as every event too, so Export is not
  // offered when the filters leave no rows.
  const handleEventsExport = async () => {
    setShowExportDialog(false);
    const filtered = filteredEvents.length < events.length;
    const eventIds = filtered
      ? filteredEvents.flatMap((e) => e.eventIDs || [])
      : undefined;
    if (filtered && eventIds.length === 0) return;
    await exportAndDownloadFile(
      exportResultsToFile,
      [clubId, meetId, csvFormat, eventIds],
      'exported_results.xlsx',
      'File was successfully exported',
      'Fetching data from the server...'
    );
  };

  const handleEventAdding = async () => {
    closeEditPopup();
    // A new event has no results, and only the event view lists an event
    // without results (the age view is built from results). So show it there.
    if (viewOption === 'event-view') {
      fetchAllEvents();
    } else {
      navigate(routes.admin.events.url(clubId, meetId, 'event-view'));
    }
  };

  const editEventType = async (event) => {
    console.log('editing event type', event);
  };

  const handlePublished = async () => {
    setFetchingEvents(true);
    await updateEventPublishingStatusWithMeetID(clubId, meetId, 'Published');
    fetchAllEvents().then(() => {
      setFetchingEvents(false);
    });
  };

  const handlePreliminary = async () => {
    setFetchingEvents(true);
    await updateEventPublishingStatusWithMeetID(clubId, meetId, 'Preliminary');
    fetchAllEvents().then(() => {
      setFetchingEvents(false);
    });
  };

  // The list for the current view has been fetched, so "nothing here" is a
  // fact and not just the moment before the rows arrive.
  const listReady = Boolean(viewOption) && !listLoading;

  return (
    <>
      <MetaTags title="SplitFast | Events" />
      <AdminTablePageLayout loading={fetchingEvents || fetchingMeet}>
        <MeetPageHeader
          title="Results"
          meetName={meet?.meetName}
          meetDate={convertDateTime(meet?.meetDate).date}
          onBackToMeet={handleBackToMeet}
          titleBadge={
            preliminaryTag ? (
              <span
                style={{
                  background: '#4767FF',
                  color: 'white',
                  fontWeight: '500',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '14px',
                }}
              >
                Preliminary
              </span>
            ) : null
          }
        >
          {userPermissionLevel >= UserPermissionLevel.Official &&
            (viewOption === 'event-view' || events.length === 0) && (
              <>
                {events.length > 0 &&
                  (preliminaryTag ? (
                    <CustomNewButton
                      onClick={handlePublished}
                      label="Mark as Final"
                      variant="success"
                      tooltip="Removes the Preliminary status badge when viewing results for this meet."
                    />
                  ) : (
                    <CustomNewButton
                      onClick={handlePreliminary}
                      label="Mark as Preliminary"
                      variant="primary2"
                      tooltip="Displays a Preliminary status badge to members when viewing results for this meet."
                    />
                  ))}
                {events.length > 0 && (
                  <CustomNewButton
                    onClick={() => setShowExportDialog(true)}
                    label="Export"
                    variant="info"
                    disabled={filteredEvents.length === 0}
                    tooltip="Export to a ResultsHQ Excel file or a SplitFast csv file."
                  />
                )}
                <CustomNewButton
                  onClick={handleAddEvent}
                  label="+ Add Event"
                  tooltip="Add a new event to this meet."
                />
              </>
            )}
        </MeetPageHeader>
        {/* The filters, and the Event View / Age View switch at the row's right
            end. The row is also shown with no rows when the meet has age
            events, so the switch is always reachable: an empty view must never
            be a dead end. */}
        {(events.length > 0 || hasAgeEvents) && (
          <div className={`${filterStyles.filterCard} ${styles.resultsFilterCard}`}>
            {events.length > 0 && (
              <>
                <div className={filterStyles.filterGroup}>
                  <label className={filterStyles.filterLabel}>Event</label>
                  <select
                    className={filterStyles.filterSelect}
                    value={eventTypeFilter}
                    onChange={(e) => setFilter('eventType', e.target.value)}
                  >
                    <option value="all">All Events</option>
                    {eventOptions.map((event) => (
                      <option key={event} value={event}>
                        {event}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={filterStyles.filterGroup}>
                  <label className={filterStyles.filterLabel}>{words.group}</label>
                  <select
                    className={filterStyles.filterSelect}
                    value={ageGroupFilter}
                    onChange={(e) => setFilter('ageGroup', e.target.value)}
                  >
                    <option value="all">All {words.values}</option>
                    {ageOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={filterStyles.filterGroup}>
                  <label className={filterStyles.filterLabel}>Gender</label>
                  <select
                    className={filterStyles.filterSelect}
                    value={genderFilter}
                    onChange={(e) => setFilter('gender', e.target.value)}
                  >
                    <option value="all">All</option>
                    {genderOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={`${filterStyles.filterResultCount} ${styles.countBesideFilters}`}>
                  Showing <strong>{filteredEvents.length}</strong> of {events.length}
                </div>
              </>
            )}
            {hasAgeEvents && (
              <div className={styles.viewSwitchEnd}>
                <ResultsViewSwitch
                  viewOption={viewOption}
                  onChange={changeViewOption}
                  words={words}
                />
              </div>
            )}
          </div>
        )}
        {/* The table is also shown with no rows when the meet has age events,
            to say why the view is empty. Nothing is reported as empty until
            the current view's list has been fetched (listReady). */}
        {events.length || (hasAgeEvents && listReady) ? (
          <EventDataTable
            columns={tableColumns}
            data={filteredEvents}
            headStyle={{
              gridTemplateColumns: '6fr 4fr 4fr 4fr 2fr',
            }}
            rowStyle={{
              gridTemplateColumns: '6fr 4fr 4fr 4fr 2fr',
            }}
            emptyState={
              events.length === 0 ? (
                <span>
                  {viewOption === 'age-view'
                    ? `No results to show in the ${words.view} yet. Events that have no results are listed in the Event View.`
                    : 'No events in this meet yet.'}
                </span>
              ) : (
                <>
                  <span>No events match the current filters.</span>
                  <CustomNewButton
                    variant="info"
                    label="Clear Filters"
                    onClick={clearFilters}
                  />
                </>
              )
            }
          />
        ) : listReady ? (
          <div style={{ textAlign: 'center' }}>No Events Results</div>
        ) : null}
      </AdminTablePageLayout>
      <EventPopup
        mode={editMode}
        clubID={clubId}
        meetID={meetId}
        showPopup={showPopup}
        closePopup={closeEditPopup}
        onSuccess={handleEventAdding}
      />
      <ExportFormatDialog
        isOpen={showExportDialog}
        format={csvFormat}
        onFormatChange={setCsvFormat}
        onExport={handleEventsExport}
        onClose={() => setShowExportDialog(false)}
      />
    </>
  );
};

Events.propTypes = {
  isOpenEditPopup: PropTypes.bool,
};

export default Events;
